import { Utils } from '../../../../../../helpers'
import { BaseModel } from '../../../../../../data/models'
import { UriFactory } from '../../../../../../core/factories'
import { JsonapiUrlGenerator, ModelMapper, BaseJsonModelInterface } from '../../../../../../model-repository/backends'
import { IOperationRequest } from '../../'
import { IOperationRequestParameter } from '../../'

type UrlStrategy = (param: IOperationRequestParameter) => string
type PayloadStrategy = (param: IOperationRequestParameter) => {}

export class JsonApiOperationRequest {

  private _createUrlStrategies: Map<string, UrlStrategy>
  private _createPayloadStrategies: Map<string, PayloadStrategy>

  constructor(private _utils: Utils,
              private _jsonapiUrlGenerator: JsonapiUrlGenerator,
              private _modelMapper: ModelMapper,
              private _uriFactory: UriFactory
  ) {
    this._createUrlStrategies = new Map<string, UrlStrategy>()
    this._createUrlStrategies.set('get', this._paramsInUrlUrlStrategy)
    this._createUrlStrategies.set('post', this._defaultJsonApiUrlStrategy)

    this._createPayloadStrategies = new Map<string, PayloadStrategy>()
    this._createPayloadStrategies.set('get', this._nullPayloadStrategy)
    this._createPayloadStrategies.set('post', this._paramsInJsonApiStrategy)
  }

  public generate(params: IOperationRequestParameter): IOperationRequest {
    return {
      httpMethod: params.httpMethod,
      returnType: params.returnType,
      url: this._createUrl(params),
      payload: this._createPayload(params)
    }
  }

  private _createUrl(params: IOperationRequestParameter): string {
    const urlGenerator = this._createUrlStrategies.get(params.httpMethod)
    return urlGenerator.call(this, params)
  }

  private _createPayload(params: IOperationRequestParameter): {} {
    const payloadGenerator = this._createPayloadStrategies.get(params.httpMethod)
    return payloadGenerator.call(this, params)
  }

  private _paramsInUrlUrlStrategy(requestParams: IOperationRequestParameter): string {
    const uri = this._uriFactory.create(this._defaultJsonApiUrlStrategy(requestParams))
    const operation = requestParams.operation.toLowerCase().trim()
    if (operation !== 'index' && operation !== '') {
      uri.addPathPart(operation)
    }
    const params = requestParams.params || {}
    Object.keys(params).forEach((key) => {
      const formattedKey = this._formatJsonApiKey(key)
      const formattedValue = this._formatSimpleValue(params[key])
      uri.addQueryStringPart(`params[${formattedKey}]`, formattedValue)
    })
    return uri.toString()
  }

  private _defaultJsonApiUrlStrategy(requestParams: IOperationRequestParameter): string {
    return this._jsonapiUrlGenerator.generateUrl(
      new requestParams.returnType().type,
      requestParams.filters || {},
      requestParams.includes || []
    )
  }

  private _nullPayloadStrategy(requestParams: IOperationRequestParameter): {} {
    return undefined
  }

  private _paramsInJsonApiStrategy(requestParams: IOperationRequestParameter): {} {
    const jsonRpcMeta = {
      meta: {
        jsonrpc: '2.0',
        method: requestParams.operation,
        params: this._serializeComplexParameters(requestParams.params)
      }
    }
    if ('requestId' in requestParams && requestParams.requestId) {
      jsonRpcMeta.meta['id'] = requestParams.requestId
    }
    return jsonRpcMeta
  }

  private _serializeComplexParameters(paramsObject: {} = {}) {
    return Object.keys(paramsObject).reduce((result, key) => {
      const serializedParam = this._serializeComplexParameter(paramsObject[key])
      result[this._formatJsonApiKey(key)] = serializedParam
      return result
    }, {})
  }

  private _serializeComplexParameter(paramData: BaseModel|{}) {
    const mapper = (paramData instanceof BaseModel)
      ? this._basemodelParameterMapper : this._objectParameterMapper
    return mapper.call(this, paramData)
  }

  private _basemodelParameterMapper(model: BaseModel) {
    if (!model.isPersisted()) {
      throw new Error('Only persisted models are supported - use hashes for plain parameters.')
    }
    return {
      data: {
        type: this._formatJsonApiType(model),
        id: model.id
      }
    }
  }

  private _objectParameterMapper(paramData: {} = {}) {
    return {
      data: {
        type: 'object',
        attributes: JSON.parse(JSON.stringify(paramData))
      }
    }
  }

  private _formatJsonApiType(model: BaseModel): string {
    return (this._modelMapper.mapToJson(model) as BaseJsonModelInterface).type
  }

  private _formatJsonApiKey(key: string) {
    return this._utils.dashCase(key)
  }

  private _formatSimpleValue(value: any) {
    let result = value
    if (Array.isArray(value)) {
      result = value.map((v) => this._formatSimpleValue(v)).join(',')
    } else if (typeof value === 'object') {
      if (value instanceof BaseModel) {
        result = value.id
      } else if ('toString' in value && value.toString instanceof Function) {
          result = value.toString()
      } else {
        throw new Error('Unsupported parameter value of type "object"!')
      }
    } else if (typeof value === 'function') {
      throw new Error('Unsupported parameter value of type "function"!')
    }
    return result
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/services/operation/operation-processors/json-api-processor/json-api-operation-request/json-api-operation-request.ts